import { useState } from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, userTimezone } from "@/lib/api";
import { InstallCard } from "@/components/InstallCard";
import { ReminderCard } from "@/components/ReminderCard";
import { needsHomeScreen, pushSupported } from "@/lib/push";
import { AvatarUpload } from "@/components/Avatar";
import { BeltPicker, ChoiceRow, FREQ, GOALS, Label, STYLES } from "@/components/ProfileInputs";

const STEPS = [
  { title: "Who's training?", sub: "This is how you'll show up on the leaderboard." },
  { title: "Your belt", sub: "Helps the AI pitch advice at your level." },
  { title: "Your training", sub: "All optional. Skip anything you like." },
  { title: "Main goal", sub: "What are you training for right now?" },
  { title: "Get the app", sub: "Install Roll SmartR on your phone so logging takes seconds." },
  { title: "Stay on track", sub: "Want a quick nudge on days you haven't logged? You can change this anytime." },
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    display_name: "", age: "", belt: "white", stripes: 0, gym: "", years_training: "",
    sessions_per_week: null, style: "both", goal: "", hide_from_leaderboard: false,
  });
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  const ageOk = !f.age || (+f.age >= 13 && +f.age <= 100);
  const canNext = step !== 0 || (f.display_name.trim().length >= 2 && ageOk);

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", {
        ...f,
        display_name: f.display_name.trim(),
        age: f.age ? +f.age : null,
        years_training: f.years_training === "" ? null : +f.years_training,
        goal: f.goal || null,
        onboarded: true,
        timezone: userTimezone(),
        notif_prompt_seen: user.notif_prompt_seen || (pushSupported() && !needsHomeScreen()),
      });
      setUser(data);
      toast.success(`You're in, ${data.display_name}. Log your first session.`);
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(false);
    }
  };

  const next = () => (step === STEPS.length - 1 ? finish() : setStep(step + 1));

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="relative z-10 max-w-md mx-auto min-h-screen bg-[#0A0A0A] flex flex-col px-6 pt-8 pb-8">
        <div className="flex items-center gap-4 h-10">
          {step > 0 ? (
            <button data-testid="onboarding-back-button" onClick={() => setStep(step - 1)} className="w-12 h-12 -ml-3 rounded-full flex items-center justify-center active:bg-[#161618] hover:bg-[#161618] transition-colors">
              <ArrowLeft size={20} />
            </button>
          ) : <div className="w-8" />}
          <div className="flex-1 flex gap-1.5" data-testid="onboarding-progress">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-[#E50914]" : "bg-[#27272A]"}`} />
            ))}
          </div>
          <div className="text-xs text-[#71717A] w-8 text-right">{step + 1}/{STEPS.length}</div>
        </div>

        <div key={step} className="flex-1 pt-10 rise">
          <h1 className="font-display text-4xl font-bold uppercase tracking-tight leading-none" data-testid="onboarding-step-title">{STEPS[step].title}</h1>
          <p className="text-[#A1A1AA] mt-3 mb-8">{STEPS[step].sub}</p>

          {step === 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <AvatarUpload user={user} onUser={setUser} name={f.display_name} className="w-20 h-20 rounded-2xl" textClass="text-3xl" testid="onboarding-avatar" />
                <div>
                  <div className="text-sm font-semibold">Profile photo</div>
                  <div className="text-xs text-[#71717A] mt-0.5">Optional. Tap to add one.</div>
                </div>
              </div>
              <div>
                <Label>Display name</Label>
                <input data-testid="onboarding-name-input" className="field" maxLength={24} placeholder="e.g. Marcus" value={f.display_name} onChange={(e) => set({ display_name: e.target.value })} autoFocus />
              </div>
              <div>
                <Label hint="Optional">Age</Label>
                <input data-testid="onboarding-age-input" className="field" type="number" inputMode="numeric" min={13} max={100} placeholder="e.g. 28" value={f.age} onChange={(e) => set({ age: e.target.value })} />
                {!ageOk && <p className="text-xs text-[#FF3B30] mt-2" data-testid="onboarding-age-error">Roll SmartR is for ages 13 and up.</p>}
              </div>
              <label className="card p-4 flex items-center justify-between gap-4 cursor-pointer">
                <div>
                  <div className="text-sm font-semibold">Show me on the leaderboard</div>
                  <div className="text-xs text-[#71717A] mt-0.5">Only your name, belt, XP and level. Change anytime.</div>
                </div>
                <Switch data-testid="onboarding-leaderboard-switch" checked={!f.hide_from_leaderboard} onCheckedChange={(v) => set({ hide_from_leaderboard: !v })} />
              </label>
            </div>
          )}

          {step === 1 && <BeltPicker belt={f.belt} stripes={f.stripes} onChange={set} />}

          {step === 2 && (
            <div className="space-y-7">
              <div>
                <Label hint="Optional">Gym / academy</Label>
                <input data-testid="onboarding-gym-input" className="field" maxLength={60} placeholder="e.g. Alliance Downtown" value={f.gym} onChange={(e) => set({ gym: e.target.value })} />
              </div>
              <div>
                <Label hint="Optional">Years training</Label>
                <input data-testid="onboarding-years-input" className="field" type="number" inputMode="decimal" min={0} max={70} step="0.5" placeholder="e.g. 1.5" value={f.years_training} onChange={(e) => set({ years_training: e.target.value })} />
              </div>
              <div>
                <Label hint="Per week">Sessions</Label>
                <ChoiceRow testid="onboarding-freq" options={FREQ.map((n) => ({ v: n, l: n === 7 ? "7+" : String(n) }))} value={f.sessions_per_week} onChange={(v) => set({ sessions_per_week: v })} className="grid grid-cols-7 gap-1.5" />
              </div>
              <div>
                <Label>I mostly train</Label>
                <ChoiceRow testid="onboarding-style" options={STYLES} value={f.style} onChange={(v) => set({ style: v })} className="grid grid-cols-3 gap-2" />
              </div>
            </div>
          )}

          {step === 3 && (
            <ChoiceRow testid="onboarding-goal" options={GOALS} value={f.goal} onChange={(v) => set({ goal: v })} className="grid grid-cols-1 gap-2 [&>button]:h-14 [&>button]:text-left [&>button]:text-base" />
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="onboarding-reminders-step">
              <ReminderCard />
              <p className="text-xs text-[#71717A]">Two reminders a day to log your session: 2pm and 10pm your time. The 10pm one is skipped if you already logged.</p>
            </div>
          )}

          {step === 4 && <InstallCard />}
        </div>

        <div className="space-y-3 pt-6">
          <button data-testid="onboarding-next-button" onClick={next} disabled={!canNext || busy} className="btn-primary">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <>{step === STEPS.length - 1 ? "Start logging" : "Continue"} <ArrowRight size={18} /></>}
          </button>
          {step >= 2 && step < STEPS.length - 1 && (
            <button data-testid="onboarding-skip-button" onClick={next} className="w-full h-10 text-sm text-[#A1A1AA] hover:text-white transition-colors">Skip</button>
          )}
          {step === 4 && (
            <p className="text-xs text-center text-[#71717A]">You can install later from your Profile.</p>
          )}
        </div>
      </div>
    </div>
  );
}
